import React, { FC } from 'react';
import styled from 'styled-components';
import { OnlineThemeType } from '../../themes/online';
import { ButtonSize, ButtonColor, ButtonType } from '../../enums/common';

function buttonSize(size: ButtonSize, stretch: boolean, theme: OnlineThemeType) {
  switch (size) {
    case ButtonSize.small:
      return `
        height: 40px;
        font-size: 16px;
        width: ${stretch ? '100%' : '200px'};
        border-radius: ${theme.borderRadiusButton.small}px;
        padding: 0 ${theme.spacing.medium}px;
      `;
    case ButtonSize.medium:
      return `
        height: 54px;
        font-size: 18px;
        width: ${stretch ? '100%' : '234px'};
        border-radius: ${theme.borderRadiusButton.medium}px;
        padding: 0 ${theme.spacing.large}px;
      `;
    case ButtonSize.large:
      return `
        height: 72px;
        font-size: 22.5px;
        width: ${stretch ? '100%' : '339px'};
        border-radius: ${theme.borderRadiusButton.large}px;
        padding: 0 ${theme.spacing.large}px;
        line-height: 1.82;
        letter-spacing: 1.13px;
      `;
    case ButtonSize.mediumLarge:
    default:
      return `
        height: 63px;
        font-size: 21px;
        min-width: ${stretch ? '100%' : '298px'};
        border-radius: ${theme.borderRadiusButton.mediumLarge}px;
        padding: 0 ${theme.spacing.large}px;
      `;
  }
}

export function buttonColor(color: ButtonColor, theme: OnlineThemeType) {
  switch (color) {
    case ButtonColor.dark:
      return `
        background-color: ${theme.colors.primaryDark2};
      `;
    case ButtonColor.alternate:
      return `
        background-color: ${theme.colors.sunbitYellow};
      `;
    case ButtonColor.gray:
      return `
        background-color: ${theme.colors.grayDark};
      `;
    case ButtonColor.light:
    case ButtonColor.default:
    default:
      return `
            background-color: ${theme.colors.light};
          `;
  }
}

function buttonDisabled(disabled: boolean, theme: OnlineThemeType) {
  if (disabled) {
    return `
      background-color: ${theme.colors.disabledButton};
    `;
  }
  return '';
}

function buttonGhost(ghostButton: boolean, theme: OnlineThemeType) {
  if (ghostButton) {
    return `
      background-color: transparent;
      border: 2px solid ${theme.colors.light};
      color: ${theme.colors.light};
    `;
  }
  return '';
}

function buttonCancel(cancelButton: boolean, theme: OnlineThemeType) {
  if (cancelButton) {
    return `
      background: transparent;
      border-bottom: 1px solid ${theme.colors.light5};
      font-size: 14.5px;
      font-weight: ${theme.font.weight.normal};
      line-height: 1.28;
      text-align: left;
      border-radius: 0;
      width: 49px;
      height: 16px;
      padding: 0;
      color: ${theme.colors.light5};
      box-shadow: none;
    `;
  }
  return '';
}

function buttonLink(linkButton: boolean, theme: OnlineThemeType) {
  if (linkButton) {
    return `
      background: transparent;
      border-bottom: 1px solid ${theme.colors.light3};
      font-size: 19px;
      font-weight: ${theme.font.weight.light};
      line-height: 1.27;
      border-radius: 0;
      padding: 0;
      width: auto;
      height: 18px;
      color: ${theme.colors.light3};
      box-shadow: none;
    `;
  }
  return '';
}

function buttonReturnBack(returnBackButton: boolean, returnBackButtonIcon: any, theme: OnlineThemeType) {
  if (returnBackButton) {
    return `
      border-radius: 0px;
      color: #8F97A8;
      background: transparent;
      box-shadow: none;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 21px;
      letter-spacing: 0em;
      height: auto;
      width: auto;
      position: relative;

      &::before {
        content: '';
        position: absolute;
        background: url(${returnBackButtonIcon});
        height: 12px;
        width: 7.409999847412109px;
        left: 0px;
        border-radius: 0px;
      }
    `;
  }
  return '';
}

// tslint:disable-next-line:variable-name
const SButton = styled.button<{
  stretch: boolean;
  disabled: boolean;
  size: ButtonSize;
  color: ButtonColor;
  ghostButton: boolean;
  cancelButton: boolean;
  linkButton: boolean;
  returnBackButton: boolean;
  returnBackButtonIcon: any;
}>(
  ({
    theme,
    stretch,
    disabled,
    size,
    color,
    ghostButton,
    cancelButton,
    linkButton,
    returnBackButton,
    returnBackButtonIcon,
  }) => {
    return `
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  box-shadow: ${disabled ? 'none' : '5.7px 5.7px 13px 0 rgba(0, 0, 0, 0.16)'};
  color: ${theme.colors.white};
  font-weight: ${theme.font.weight.bold};
  border: 1px solid transparent;
  outline: none;
  cursor: pointer;
  user-select: none;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.6;
  letter-spacing: normal;
  text-align: center;
  ${buttonSize(size, stretch, theme)}
  ${buttonColor(color, theme)}
  ${buttonDisabled(disabled, theme)}
  ${buttonGhost(ghostButton, theme)}
  ${buttonCancel(cancelButton, theme)}
  ${buttonLink(linkButton, theme)}
  ${buttonReturnBack(returnBackButton, returnBackButtonIcon, theme)}


  @media only screen and (min-width: 800px) {
    &:hover {
      &:before {
        opacity: ${returnBackButtonIcon ? '1' : '0.1'};
      }
    }
  }
`;
  }
);

export interface IButtonProps {
  id?: string;
  onClick: React.MouseEventHandler;
  onKeyUp?: React.KeyboardEventHandler;
  stretch?: boolean;
  dataAt?: string;
  dataTestId?: string;
  dataDdAction?: string;
  color?: ButtonColor;
  ghostButton?: boolean;
  cancelButton?: boolean;
  size?: ButtonSize;
  disabled?: boolean;
  className?: string;
  type?: ButtonType;
  linkButton?: boolean;
  returnBackButton?: boolean;
  returnBackButtonIcon?: any;
  margin?: string;
  secondary?: boolean;
  children?: React.ReactNode;
}

export const Button: FC<IButtonProps> = ({
  id,
  dataAt,
  onClick,
  onKeyUp,
  type,
  disabled,
  ghostButton,
  stretch,
  size,
  color,
  className,
  cancelButton,
  linkButton,
  returnBackButton,
  returnBackButtonIcon,
  children,
  dataTestId,
  dataDdAction,
}) => {
  return (
    <SButton
      {...{
        id,
        type,
        onClick,
        onKeyUp,
        'data-at': dataAt,
        ghostButton,
        cancelButton,
        linkButton,
        returnBackButton,
        returnBackButtonIcon,
        disabled,
        stretch,
        size,
        color,
        className,
        'data-testid': dataTestId,
        'data-dd-action': dataDdAction,
      }}
    >
      {children}
    </SButton>
  );
};

Button.defaultProps = {
  id: '',
  stretch: false,
  disabled: false,
  ghostButton: false,
  cancelButton: false,
  linkButton: false,
  returnBackButton: false,
  returnBackButtonIcon: '',
  color: ButtonColor.default,
  size: ButtonSize.mediumLarge,
  type: ButtonType.Button,
};

export default Button;
